// utils/formulaTokens.js
//
// The web half of calculated elements: everything the formula builder, the
// item form's calculated row and the rule modal need from the shared formula
// module (utils/formula, vendored from the API), wired to THIS repo's card
// cache and units module.
//
// Three jobs, all pure:
//   * the reference resolver — `describe(path)` for the analyser/evaluator,
//     mirroring the API's refResolver (utils/automations/formulaFields.js):
//     own elements (child definitions override inherited ones, the one
//     merge rule in utils/cards), the
//     `createdAt`/`updatedAt` terminals, one-hop dot-walks through a
//     card-lookup (walked element or `title`/`createdAt`/`updatedAt`; a
//     multi-value lookup yields a list);
//   * the token tree for TokenInput's pill menu;
//   * analysis + preview: parse, type against the card, classify the result
//     against the target element through the coercion mirror, and evaluate
//     against a bag of values (the card editor's "Try it", the item form's
//     "after save" hint, the calculated-value modal).
//
// Every analyse/evaluate/renderText call injects BOTH vendored registries —
// `{ units: formulaUnits, currency: formulaCurrency }` — exactly as the API
// injects its own; a money formula without the currency module throws.
//
// The API is the authority — it re-validates on save and evaluates on write.
// This module exists so the builder can say "no" (and why) before the round
// trip, and so a preview here and a value there never disagree: both run the
// same byte-identical module.

import { normalizeId } from './ids';
import { createElementResolver, mergeElements } from './cards';
import { FormulaError, analyze, columnValuesOf, dataTypeOf, describeElement, evaluate, formulaCurrency, formulaUnits, parse, renderText, serialize, toStored, typeLabel } from './formula';
import { classify } from '../config/elementCoercion';
import { allowedMeasures, formatMeasurement, getMeasure, getUnit, isMultiMeasure, isOwnUnitId, ownUnitsOf } from './units';
import { defaultCodeOf, formatCurrency, isCurrencyValue, normalizeCurrency, readCurrency, toMajor, toMinor } from './currency';
import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime } from './dateDisplay';
import { formatBooleanValue } from './elements';


const labelOf = element => element?.label?.singular || element?.name || '';
const namedElements = elements => (Array.isArray(elements) ? elements : []).filter(el => el?.name);
const isLookup = element => element?.dataType === 'card-lookup';
// A file element is never a formula's or a template's input (PR 10b): its
// value is an attachment id, which no formula reads and no value should copy.
// Nor is a "Me too" count (KPR-390): it changes without the item being saved,
// so a value computed or copied from it goes stale on the next confirmation —
// the vendored formula module refuses the reference by name.
// Nor is a repeating group, as a VALUE (KPR-679): its entries are read through
// their own references (KPR-686, below) — `{{line}}` the entries, and
// `{{line.amount}}` a column — by a calculated element or column only.
const NEVER_READ_TYPES = new Set(['file', 'confirmations', 'repeating-group']);
const offeredElements = elements => namedElements(elements).filter(el => !NEVER_READ_TYPES.has(el?.dataType));
const isGroup = element => element?.dataType === 'repeating-group';
// What a picker may read off a token without resolving it (KPR-350): the
// measure of a measurement element, so convert()'s unit list offers that
// measure's units. Spread onto a token node; the menu ignores it.
// An element that allows several measures (KPR-1085) gives no hint: its value
// may be any of them, so every unit stays on offer.
const tokenHints = element => (element?.dataType === 'measurement' && typeof element?.options?.measure === 'string' && element.options.measure
	&& !isMultiMeasure(element.options)
	? { measure: element.options.measure }
	: {});
const groupColumnsOf = group => (Array.isArray(group?.options?.columns) ? group.options.columns : []).filter(c => c?.name);
// Mirrors keepr-api utils/automations/formulaFields.js groupNotHere: a surface
// that reads no entries (a chart's per-item calculation, a quick add template)
// refuses a group by name.
const groupNotHere = path => new FormulaError(`{{${path.join('.')}}} is a repeating group — only a calculated element on its own card, or one of its calculated columns, can read its entries.`, 0);

const WALK_TERMINALS = {
	title: { name: 'title', dataType: 'text-small', label: { singular: 'Title' }, options: {} },
	createdAt: { name: 'createdAt', dataType: 'date-time', label: { singular: 'Created' }, options: {} },
	updatedAt: { name: 'updatedAt', dataType: 'date-time', label: { singular: 'Updated' }, options: {} },
};
const OWN_TERMINALS = { createdAt: WALK_TERMINALS.createdAt, updatedAt: WALK_TERMINALS.updatedAt };
// Own-property reads everywhere a NAME indexes a plain object: `{{toString}}`
// must not find Object.prototype (the module refuses only the three
// dangerous keys; junk like `valueOf` is ours to refuse).
const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const own = (obj, key) => (hasOwn(obj, key) ? obj[key] : undefined);

/**
 * The element resolver every formula surface uses: the platform's ONE merge
 * rule (utils/cards createElementResolver — a child card's re-declaration
 * of an inherited name wins, in the ancestor's position), exactly as the
 * engine reads it. The name survives from when the app cache merged the
 * other way round; it is the same function now.
 */
export const createLastWinsResolver = createElementResolver;

/**
 * The resolver a formula surface should use: last-wins over the app cache's
 * index when it has the card, else over the cards the caller was handed
 * (the cache may still be loading, or lack a card this manager cannot read).
 */
export function formulaElementResolver(cardDefinitions, collectionCards = []) {
	// The cache's element sets fold into both: a set's element is a valid
	// formula source (and a valid date bound) like an inherited one.
	const elementSetsById = cardDefinitions?.elementSetsById || null;
	const fromCache = createLastWinsResolver(cardDefinitions?.cardsById || {}, elementSetsById);
	const local = createLastWinsResolver(Object.fromEntries((collectionCards || []).map(card => [normalizeId(card?._id), card]).filter(([id]) => !!id)), elementSetsById);
	return id => {
		const cached = fromCache(id);
		return cached.length ? cached : local(id);
	};
}

export const isFormulaDriven = element => element?.options?.drivenFrom?.kind === 'formula';

// Inherited elements then own, merged by the platform's one rule
// (utils/cards mergeElements); re-exported so the formula surfaces keep
// their import.
export { mergeElements };

/** The merged element set of the card being edited: parents + form state. */
export function mergedElementsForCard({ ownElements, parentCardId, getElementsForCard }) {
	const parentId = normalizeId(parentCardId);
	const inherited = parentId && typeof getElementsForCard === 'function' ? (getElementsForCard(parentId) || []) : [];
	return mergeElements(inherited, ownElements);
}

function walkedElements(lookup, getElementsForCard) {
	const targetId = normalizeId(lookup?.options?.lookupCardId);
	if (!targetId || typeof getElementsForCard !== 'function') return [];
	return offeredElements(getElementsForCard(targetId));
}

/**
 * `describe(path)` for the analyser and evaluator. `excludeName` is the
 * element being authored — a formula never reads itself.
 *
 * `groups` (KPR-686, keepr-api formulaFields refResolver mirrored): a
 * calculated element (or column) reads the card's repeating groups —
 * `{{line}}` their entries, `{{line.amount}}` a column's values. Without it a
 * group is refused by name. A walk never reads another card's group.
 * `walks: false` (a calculated column, which reads one item — its own)
 * refuses a lookup hop with the API's sentence.
 */
export function createFormulaRefResolver(mergedElements, getElementsForCard, { excludeName = null, groups = false, walks = true } = {}) {
	const byName = new Map(namedElements(mergedElements).map(el => [el.name, el]));
	return function describe(path) {
		if (!Array.isArray(path) || !path.length) return null;
		if (path.length === 1) {
			const el = byName.get(path[0]);
			if (el) {
				if (el.name === excludeName) return null;
				if (isGroup(el) && !groups) throw groupNotHere(path);
				return describeElement(el);
			}
			if (hasOwn(OWN_TERMINALS, path[0])) return describeElement(OWN_TERMINALS[path[0]]);
			return null;
		}
		const [via, field] = path;
		const head = byName.get(via);
		if (isGroup(head)) {
			if (!groups) throw groupNotHere(path);
			const column = groupColumnsOf(head).find(c => c.name === field);
			return column ? describeElement(column, { list: true, fromEntries: true }) : null;
		}
		const lookup = head;
		if (!lookup || !isLookup(lookup)) return null;
		if (!walks) throw new FormulaError(`{{${path.join('.')}}} follows a lookup to another item — a calculated column reads its own entry and its item, never another item.`, 0);
		const target = walkedElements(lookup, getElementsForCard).find(el => el.name === field) || own(WALK_TERMINALS, field);
		if (!target) return null;
		if (isGroup(target)) throw groupNotHere(path);
		return describeElement(target, { list: !!lookup.options?.allowMultiple });
	};
}

/**
 * The entry scope of a group COLUMN's own formula (KPR-686): the group's name
 * and its columns as the formula module types them, the column being
 * authored left out (a column never reads itself). `columns` are the group's
 * columns as the editor holds them, each with the name the API will give it.
 */
export function entryScopeOf(group, columns, { excludeName = null } = {}) {
	const scope = Object.create(null);
	(columns || []).filter(c => c?.name && c.name !== excludeName).forEach((c) => { scope[c.name] = describeElement(c); });
	return { group: group?.name || null, columns: scope };
}

/**
 * The token tree for the formula editor: every element of the card (the one
 * being authored excluded), lookups walkable one hop, then the record's own
 * timestamps. Pill labels are the elements' labels; a walk reads
 * "Vehicle · Weight".
 *
 * `walks: false` offers no walk at all — a lookup is then its own id, a
 * pill like any element — for a surface that refuses a hop to another item
 * (a chart's per-item calculation, charts A8: keepr-api spec.js
 * `formula_lookup_hop`), so the menu never offers what the server refuses.
 */
export function buildFormulaTokens(mergedElements, getElementsForCard, { excludeName = null, walks = true, groups = false, entry = null } = {}) {
	const own = offeredElements(mergedElements)
		.filter(el => el.name !== excludeName)
		.map(el => {
			const node = { token: `{{${el.name}}}`, label: labelOf(el), menuLabel: labelOf(el), ...tokenHints(el) };
			if (!isLookup(el) || !walks) return node;
			const titleNode = { token: `{{${el.name}.title}}`, label: `${labelOf(el)} · Title`, menuLabel: 'Title of the linked record' };
			const children = walkedElements(el, getElementsForCard).map(child => ({
				token: `{{${el.name}.${child.name}}}`,
				label: `${labelOf(el)} · ${labelOf(child)}`,
				menuLabel: labelOf(child),
				...tokenHints(child),
			}));
			return {
				...node,
				children: [
					{ ...node, menuLabel: `${labelOf(el)} (the link itself)` },
					titleNode,
					...children,
				],
			};
		});
	// A REPEATING GROUP (KPR-686): its entries — counted, or tested with any()
	// and all() — and each column's values, which sum(), avg(), min(), max()
	// and count() read.
	const groupNodes = groups ? namedElements(mergedElements).filter(isGroup).map(g => ({
		label: labelOf(g),
		menuLabel: labelOf(g),
		children: [
			{ token: `{{${g.name}}}`, label: `${labelOf(g)} (the entries)`, menuLabel: 'The entries — count(), any(), all()' },
			...groupColumnsOf(g).filter(c => !NEVER_READ_TYPES.has(c.dataType)).map(c => ({
				token: `{{${g.name}.${c.name}}}`,
				label: `${labelOf(g)} · ${labelOf(c)}`,
				menuLabel: labelOf(c),
				...tokenHints(c),
			})),
		],
	})) : [];
	// A column's own formula reads its ENTRY first: the entry's other columns.
	const entryNodes = entry && Array.isArray(entry.columns) && entry.columns.length ? [{
		label: `This ${entry.label || 'entry'}`,
		children: entry.columns.filter(c => c?.name && c.name !== entry.excludeName && !NEVER_READ_TYPES.has(c.dataType)).map(c => ({
			token: `{{${c.name}}}`,
			label: labelOf(c),
			menuLabel: labelOf(c),
			...tokenHints(c),
		})),
	}] : [];
	return [
		...entryNodes,
		...own,
		...groupNodes,
		{
			// "Item", never "record" in the interface (standards §11 The words).
			label: 'This item',
			children: [
				{ token: '{{createdAt}}', label: 'Created' },
				{ token: '{{updatedAt}}', label: 'Updated' },
			],
		},
	];
}

// ---------------------------------------------------------------- quick add

const QUICK_ADD_USER_FIELDS = {
	name: { name: 'name', dataType: 'text-small', label: { singular: 'Name' }, options: {} },
	email: { name: 'email', dataType: 'text-small', label: { singular: 'Email' }, options: {} },
	// The account id: text to the formula language (an id concatenates like
	// any handle); on a `user` element it is the whole formula — "me".
	id: { name: 'id', dataType: 'text-small', label: { singular: 'Account id' }, options: {} },
};

/** The one formula a `user` element takes: exactly the signed-in caller. */
export const USER_ID_TOKEN = '{{user.id}}';

/** Is this formula text exactly `{{user.id}}` (whitespace aside)? */
export const isUserIdFormula = text => typeof text === 'string' && /^\s*\{\{\s*user\.id\s*\}\}\s*$/.test(text);

/**
 * The one "me" a CARD-LOOKUP element takes: the signed-in person's own
 * record of the lookup's card (keepr-api utils/lookupMe.js). Like
 * {{user.id}} on a user element it is the WHOLE formula, settled before
 * analysis; inside a larger expression it is an ordinary {{me.<element>}}.
 */
export const ME_ID_TOKEN = '{{me.id}}';

/** Is this formula text exactly `{{me.id}}` (whitespace aside)? */
export const isMeIdFormula = text => typeof text === 'string' && /^\s*\{\{\s*me\.id\s*\}\}\s*$/.test(text);

/**
 * `describe(path)` for a QUICK ADD TEMPLATE formula — the API's template
 * resolver (utils/quickAddTemplates/resolve.js) mirrored:
 *
 *   {{el}}          the template's own resolved default → the element's type
 *   {{lookup.el}}   one hop through a card-lookup default (the base rule)
 *   {{last.el}}     the most recent matching item; title/createdAt/updatedAt too
 *   {{user.name}}   the signed-in user's display name; {{user.email}} — text;
 *                   {{user.id}} the account id (a `user` element's "me")
 *   {{me.el}}       one hop into the caller's LINKED ITEM in this collection
 *                   (the item whose account link names them), typed against
 *                   `meElements` — the merged elements of the collection's
 *                   identity-carrying cards (utils/userElements meElementsOf);
 *                   `me.title` / `me.createdAt` / `me.updatedAt` too. With no
 *                   such card the root resolves nothing (the API refuses it).
 *   {{source.el}}   the item being DUPLICATED — only in a duplicate profile
 *                   (`source: true`); read like `last`, typed against the
 *                   card's own elements. Elsewhere it says how to get it.
 *   {{createdAt}} / {{updatedAt}}   REFUSED: a new item has none; use now()
 *
 * An element literally named `last`, `source`, `user` or `me` wins over the root, as
 * the template renderer rules. `{{last.<self>}}` is legitimate (the previous
 * reading of the very element being defaulted), so only the ONE-segment self
 * reference is excluded.
 */
export function createQuickAddRefResolver(mergedElements, getElementsForCard, { excludeName = null, meElements = null, source = false } = {}) {
	const elements = namedElements(mergedElements);
	const byName = new Map(elements.map(el => [el.name, el]));
	const base = createFormulaRefResolver(mergedElements, getElementsForCard, { excludeName });
	const meByName = meElements instanceof Map ? meElements : new Map(namedElements(meElements).map(el => [el.name, el]));
	return function describe(path) {
		if (!Array.isArray(path) || !path.length) return null;
		const [root, field] = path;
		if (path.length === 1 && !byName.has(root) && hasOwn(OWN_TERMINALS, root)) {
			throw new FormulaError(`{{${root}}} is not available on a new item — use now() instead.`);
		}
		if (path.length === 2 && !byName.has(root)) {
			if (root === 'last') {
				const target = byName.get(field) || own(WALK_TERMINALS, field);
				return target ? describeElement(target) : null;
			}
			if (root === 'source') {
				if (!source) {
					throw new FormulaError('{{source.…}} reads the item being duplicated — turn on "Duplicate profile" to use it.');
				}
				const target = byName.get(field) || own(WALK_TERMINALS, field);
				return target ? describeElement(target) : null;
			}
			if (root === 'user') {
				const target = own(QUICK_ADD_USER_FIELDS, field);
				return target ? describeElement(target) : null;
			}
			if (root === 'me') {
				if (!meByName.size) {
					throw new FormulaError('{{me.…}} reads your own record here, and no card in this collection has an account link (a User element marked as the account link).');
				}
				const target = meByName.get(field) || own(WALK_TERMINALS, field);
				return target ? describeElement(target) : null;
			}
		}
		return base(path);
	};
}

/**
 * The token tree for a quick add formula: the card's own elements (lookups
 * walkable one hop) exactly as buildFormulaTokens offers them, then a "Last
 * item" group with every element plus Title/Created/Updated as
 * `{{last.<name>}}`, a "Signed-in user" group, and — when the collection has
 * a card with an account link — a "Your record" group with `{{me.<name>}}`
 * over that vocabulary (`meElements`). No "This item" group — a new item
 * has no timestamps yet.
 *
 * In a duplicate profile (`source: true`) an "Item being duplicated" group
 * leads the roots with `{{source.<name>}}` — what the profile copies from.
 *
 * A `user` TARGET element (`targetElement`) takes exactly one formula, the
 * caller: its tree is the single "Me" token (`{{user.id}}`) and nothing else,
 * because nothing else computes an account (the API refuses anything else).
 */
export function buildQuickAddTokens(mergedElements, getElementsForCard, { excludeName = null, meElements = null, targetElement = null, lookupMe = false, source = false } = {}) {
	// Every name blocks a namespace it collides with; only the offered ones
	// (never a file element) become tokens.
	const names = new Set(namedElements(mergedElements).map(el => el.name));
	const elements = offeredElements(mergedElements);
	if (targetElement?.dataType === 'user') {
		return names.has('user') ? [] : [{ token: USER_ID_TOKEN, label: 'Me', menuLabel: 'Me (the signed-in user)' }];
	}
	// A card-lookup whose card can be somebody's record (`lookupMe`, the
	// caller's lookupFamilyHasIdentity answer) leads with "My record":
	// {{me.id}}, the signed-in person's own record of that card.
	const meIdTokens = targetElement?.dataType === 'card-lookup' && lookupMe && !names.has('me')
		? [{ token: ME_ID_TOKEN, label: 'My record', menuLabel: 'My record (the signed-in person\'s)' }]
		: [];
	const ownTokens = [...meIdTokens, ...buildFormulaTokens(mergedElements, getElementsForCard, { excludeName })
		.filter(node => node.token)];
	const groups = [];
	if (source && !names.has('source')) {
		groups.push({
			label: 'Item being duplicated',
			children: [
				...elements.map(el => ({ token: `{{source.${el.name}}}`, label: `Duplicated item · ${labelOf(el)}`, menuLabel: labelOf(el) })),
				{ token: '{{source.title}}', label: 'Duplicated item · Title', menuLabel: 'Title' },
				{ token: '{{source.createdAt}}', label: 'Duplicated item · Created', menuLabel: 'Created' },
				{ token: '{{source.updatedAt}}', label: 'Duplicated item · Updated', menuLabel: 'Updated' },
			],
		});
	}
	if (!names.has('last')) {
		groups.push({
			label: 'Last item',
			children: [
				...elements.map(el => ({ token: `{{last.${el.name}}}`, label: `Last item · ${labelOf(el)}`, menuLabel: labelOf(el) })),
				{ token: '{{last.title}}', label: 'Last item · Title', menuLabel: 'Title' },
				{ token: '{{last.createdAt}}', label: 'Last item · Created', menuLabel: 'Created' },
				{ token: '{{last.updatedAt}}', label: 'Last item · Updated', menuLabel: 'Updated' },
			],
		});
	}
	if (!names.has('user')) {
		groups.push({
			label: 'Signed-in user',
			children: [
				{ token: '{{user.name}}', label: 'User · Name', menuLabel: 'Name' },
				{ token: '{{user.email}}', label: 'User · Email', menuLabel: 'Email' },
			],
		});
	}
	const meList = (meElements instanceof Map ? [...meElements.values()] : namedElements(meElements)).filter(el => !NEVER_READ_TYPES.has(el?.dataType));
	if (!names.has('me') && meList.length) {
		groups.push({
			label: 'Your record',
			children: [
				...meList.map(el => ({ token: `{{me.${el.name}}}`, label: `Your record · ${labelOf(el)}`, menuLabel: labelOf(el) })),
				{ token: '{{me.title}}', label: 'Your record · Title', menuLabel: 'Title' },
				{ token: '{{me.createdAt}}', label: 'Your record · Created', menuLabel: 'Created' },
				{ token: '{{me.updatedAt}}', label: 'Your record · Updated', menuLabel: 'Updated' },
			],
		});
	}
	return [...ownTokens, ...groups];
}

/**
 * The matrix's `sourceOptions` for a formula RESULT type — the API's
 * formulaFields resultSourceOptions, mirrored: a measurement's measure, or
 * the currencies a money result may hold (money fills a currency element
 * only when the element allows every one of them).
 */
function resultSourceOptions(type) {
	// A result that may be in several measures (KPR-1085) needs a target that
	// allows every one of them, as money's currencies do.
	if (type?.type === 'measure') return Array.isArray(type.measures) ? { measure: type.measures[0], measures: type.measures } : { measure: type.measure };
	if (type?.type === 'money') return { currencies: type.currencies || [] };
	return null;
}

/** The web's answer to "can this result land in that element?" — the API's rule, mirrored. */
export function describeResultForTarget(type, element, refs = []) {
	// A `bool` result is storable now that the platform has a yes/no element:
	// the shared module maps it to 'boolean' and the coercion matrix decides
	// which targets take it, the same as any other result type. Nothing here
	// tells anyone to wrap a comparison in if(…, "Yes", "No") any more.
	const resultDataType = dataTypeOf(type);
	if (!resultDataType) {
		if (type?.type === 'list') return { ok: false, message: 'The result is a list — wrap it in sum(), avg(), min(), max() or count().' };
		if (type?.type === 'entries') {
			// A group's entries (KPR-686), the API's sentence mirrored.
			const g = type.group || 'group';
			return { ok: false, message: `The result is a repeating group's entries — total a column with sum({{${g}.<column>}}), count them with count({{${g}}}), or test them with any() or all().` };
		}
		return { ok: false, message: 'The result is always blank.' };
	}
	const targetType = element?.dataType || 'text-small';
	// A CLOCK RESULT READS AS TEXT — everywhere but a `time` element. Mirrors
	// utils/automations/formulaFields.js classifyFormulaForTarget: `time`
	// became a result type of its own in this release, and before it a
	// formula reading a time element analysed to `text`, so every stored
	// config filling a text-small / choice / phone / email / location /
	// boolean element from a clock settled its transform through the TEXT
	// rows. A stored clock IS a plain 'HH:mm' string; the matrix's own `time`
	// rows are about a time ELEMENT driving another element and stay as they
	// are. If this drifts from the API the editor refuses what the API saves.
	const clockAsText = type?.type === 'time' && targetType !== 'time';
	if (targetType === 'choice') {
		if (type.type !== 'text' && !clockAsText) return { ok: false, message: `A choice element needs a text result, not ${typeLabel(type)}.` };
		return { ok: true, class: 'exact', transforms: ['none'], note: 'Stored when it matches one of the configured choices; otherwise left blank.' };
	}
	if (resultDataType === 'card-lookup') {
		if (targetType !== 'card-lookup') return { ok: false, message: 'A card lookup result can only fill a card-lookup element.' };
		const targetLookup = normalizeId(element?.options?.lookupCardId);
		const foreign = refs.find(ref => ref.type?.type === 'lookup' && normalizeId(ref.type.options?.lookupCardId) !== targetLookup);
		if (foreign) return { ok: false, message: `{{${foreign.path.join('.')}}} points at a different card than this element.` };
		return { ok: true, class: 'exact', transforms: ['none'] };
	}
	const cls = classify(clockAsText ? 'text-large' : resultDataType, targetType, {
		sourceOptions: resultSourceOptions(type),
		targetOptions: element?.options || {},
	});
	if (cls.class === 'unmappable') return { ok: false, message: `The result is ${typeLabel(type)}, but ${cls.reason}` };
	return { ok: true, class: cls.class, transforms: cls.transforms };
}

/**
 * Parse + type-check + target classification in one call. Never throws for
 * a formula problem: `{ ok:false, error:{ message, position } }` instead.
 * `resolveRef` overrides the reference resolver (a quick add template's
 * roots differ from a calculated element's); the default is unchanged.
 */
export function analyzeFormula(text, { mergedElements, getElementsForCard, targetElement, resolveRef: resolveRefOverride, groups = true, entry = null } = {}) {
	if (typeof text !== 'string' || !text.trim()) return { ok: false, empty: true, error: null };
	try {
		const ast = parse(text);
		// A calculated element reads the card's groups (KPR-686); a group
		// COLUMN's own formula (`entry`: entryScopeOf) reads its entry first and
		// follows no lookup.
		const resolveRef = typeof resolveRefOverride === 'function'
			? resolveRefOverride
			: createFormulaRefResolver(mergedElements, getElementsForCard, { excludeName: entry ? null : targetElement?.name, groups, walks: !entry });
		const analysis = analyze(ast, { resolveRef, units: formulaUnits, currency: formulaCurrency, ...(entry ? { entry } : {}) });
		const target = describeResultForTarget(analysis.type, targetElement, analysis.refs);
		const byName = new Map(namedElements(mergedElements).map(el => [el.name, el]));
		const ownRefs = [...new Set(analysis.refs.filter(ref => !ref.column && (ref.path.length === 1 || isGroup(byName.get(ref.path[0])))).map(ref => ref.path[0]))];
		const columnRefs = [...new Set(analysis.refs.filter(ref => ref.column).map(ref => ref.path[0]))];
		const walks = [...new Set(analysis.refs.filter(ref => !ref.column && ref.path.length === 2 && !isGroup(byName.get(ref.path[0]))).map(ref => ref.path[0]))];
		return {
			ok: target.ok,
			ast,
			canonical: serialize(ast),
			type: analysis.type,
			typeLabel: typeLabel(analysis.type),
			refs: analysis.refs,
			ownRefs,
			columnRefs,
			walks,
			timeDependent: analysis.timeDependent,
			target,
			error: target.ok ? null : { message: target.message, position: null },
		};
	} catch (err) {
		if (err?.isFormulaError) return { ok: false, error: { message: err.message, position: err.position } };
		throw err;
	}
}

function defaultUnitOf(options) {
	const measure = getMeasure(options?.measure);
	if (!measure) return undefined;
	const unit = getUnit(options?.defaultUnit);
	return unit && unit.measure === measure.id ? unit.id : measure.canonical;
}

/**
 * Evaluates a formula against a bag of values (`values[name]` for own
 * elements, `walkValues[via][field]` for walks — absent walks read blank).
 * Returns the STORED shape the API would write plus its display text.
 */
export function previewFormula(text, { mergedElements, getElementsForCard, targetElement, values = {}, walkValues = {}, entryValues = null, blanks, now, transform, groups = true, entry = null } = {}) {
	const analysis = analyzeFormula(text, { mergedElements, getElementsForCard, targetElement, groups, entry });
	if (!analysis.ok) return { ok: false, analysis };
	const describe = createFormulaRefResolver(mergedElements, getElementsForCard, { excludeName: entry ? null : targetElement?.name, groups, walks: !entry });
	const byName = new Map(namedElements(mergedElements).map(el => [el.name, el]));
	const read = path => {
		if (path.length === 1) return own(values, path[0]);
		// A group's column (KPR-686): one value per entry the bag holds.
		if (isGroup(byName.get(path[0]))) return columnValuesOf(own(values, path[0]), path[1]);
		const via = own(walkValues, path[0]);
		return via ? own(via, path[1]) : undefined;
	};
	// A group COLUMN's own formula reads one entry: `entryValues` is its columns.
	const entryOption = entry ? { entry: { columns: entry.columns, value: { elements: entryValues || {} } } } : {};
	let evaluated;
	try {
		evaluated = evaluate(analysis.ast, { read, describe: path => { try { return describe(path); } catch (e) { return null; } }, units: formulaUnits, currency: formulaCurrency, now, blanks, ...entryOption });
	} catch (err) {
		return { ok: false, analysis };
	}
	const { value, warnings: raw } = evaluated;
	const warnings = [...new Set(raw)];
	const unit = targetElement?.dataType === 'measurement' ? defaultUnitOf(targetElement.options) : undefined;
	// A target that allows several measures (KPR-1085) stores the answer in its
	// own measure, the API's storeResult rule; one it does not allow is blank.
	const measures = targetElement?.dataType === 'measurement' ? allowedMeasures(targetElement.options || {}) : undefined;
	const transformType = transform || (analysis.target.transforms || ['none'])[0];
	// An own unit (KPR-1109) lands only in the target's unit of the same id
	// AND name (the API's storeResult): another element may name that id otherwise.
	const ownUnits = measures ? ownUnitsOf(targetElement.options || {}) : undefined;
	let stored = applyPreviewTransform(toStored(value, { units: formulaUnits, unit, measures, ownUnits }), transformType, { tagged: value, targetElement });
	if (stored === '' && value && value.t === 'measure' && isOwnUnitId(value.measure) && Array.isArray(measures) && measures.length) {
		// Named as the answer's element names it, the API's sentence.
		warnings.push(`the answer is in ${value.own?.plural || value.measure.slice(4).replace(/-/g, ' ')}, a unit this element does not have`);
	} else if (stored === '' && value && value.t === 'measure' && Array.isArray(measures) && measures.length > 1 && !measures.includes(value.measure)) {
		warnings.push(`the answer is a ${value.measure}, which this element does not allow`);
	}
	// A currency element takes only its own currencies and bounds — the API's
	// guardCurrencyTarget blanks anything else with a warning; so does this.
	if (targetElement?.dataType === 'currency' && stored !== '') {
		try {
			stored = normalizeCurrency(stored, targetElement.options || {}) || '';
		} catch (err) {
			warnings.push(err?.message || 'the amount does not fit the element');
			stored = '';
		}
	}
	// A choice element stores only one of its configured choices — the API
	// blanks anything else and records a warning; so does the preview.
	if (targetElement?.dataType === 'choice' && stored !== '') {
		const allowed = (targetElement.options?.choices || []).map(c => (typeof c === 'string' ? c : (c?.value !== undefined ? c.value : c?.label))).filter(v => v !== undefined && v !== null).map(String);
		if (!allowed.includes(String(stored))) {
			warnings.push(`"${stored}" is not one of the element's choices`);
			stored = '';
		}
	}
	return { ok: true, analysis, value: stored, text: formatCalculatedValue(targetElement, stored), warnings, transform: transformType };
}

/**
 * The values bag a record's preview reads: its elements, plus the
 * createdAt/updatedAt terminals unless an element literally owns the name
 * (the element wins, as it does for the API's read).
 */
export function previewValuesOf(item, mergedElements) {
	const names = new Set(namedElements(mergedElements).map(el => el.name));
	const out = { ...(item?.elements || {}) };
	if (!names.has('createdAt')) out.createdAt = item?.createdAt;
	if (!names.has('updatedAt')) out.updatedAt = item?.updatedAt;
	return out;
}

/**
 * The result→target coercion the API applies after evaluation
 * (utils/automations/coercion.js applyTransform), mirrored for the preview
 * so "after save" here is what the save stores. Blank passes through.
 */
export function applyPreviewTransform(stored, transformType, { tagged, targetElement } = {}) {
	if (stored === '' || stored === null || stored === undefined) return '';
	const isoDatePart = v => {
		const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
		if (m) return m[1];
		const d = new Date(String(v));
		return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
	};
	// A measurement rendered to text formats the way the API's coercion does
	// (formatMeasurement with the value's own unit as the default, no display
	// rules) — NOT the module's renderText, whose natural-precision rule
	// prints 8.4375 lb where the API stores "8.4 lb".
	const asText = () => {
		if (tagged && tagged.t === 'measure' && stored && typeof stored === 'object') {
			return formatMeasurement(stored, { measure: tagged.measure, defaultUnit: stored.unit }) || '';
		}
		// Money likewise: the API's toText formats the STORED value with no
		// element context (the unambiguous symbol, "CA$12.00"), which is not
		// always renderText's answer — that one keeps an element's own symbol
		// for a value read straight from it.
		if (isCurrencyValue(stored)) return formatCurrency(stored, null) || '';
		return tagged ? renderText(tagged, { units: formulaUnits, currency: formulaCurrency }) : String(stored);
	};
	switch (transformType || 'none') {
		case 'none': return stored;
		case 'toText': case 'dateToText': return asText();
		case 'truncate': return asText().slice(0, 255);
		case 'stripFormatting': return asText();
		case 'dateTimeToDate': return isoDatePart(stored);
		case 'dateToDateTime': return /^\d{4}-\d{2}-\d{2}$/.test(String(stored)) ? `${stored}T00:00:00.000Z` : stored;
		case 'numberToDate': { const d = new Date(Number(stored)); return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10); }
		case 'numberToDateTime': { const d = new Date(Number(stored)); return Number.isNaN(d.getTime()) ? '' : d.toISOString(); }
		case 'dateToNumber': { const t = new Date(String(stored)).getTime(); return Number.isNaN(t) ? '' : t; }
		case 'measurementToNumber': {
			// The magnitude in the value's own (preferred) unit — the unit the
			// API reads as the source's default for a formula result.
			if (stored && typeof stored === 'object' && typeof stored.value === 'number') return stored.value;
			return stored;
		}
		case 'numberToMeasurement': {
			const unit = defaultUnitOf(targetElement?.options);
			const n = Number(stored);
			if (!Number.isFinite(n) || !unit) return '';
			try { return { value: n, unit, base: formulaUnits.toBase(n, unit) }; } catch (e) { return ''; }
		}
		// Mirrors the API coercion's currencyToNumber / numberToCurrency.
		case 'currencyToNumber': {
			const money = readCurrency(stored, null);
			const out = money ? toMajor(money.amount, money.currency) : NaN;
			return Number.isFinite(out) ? out : '';
		}
		case 'numberToCurrency': {
			const code = defaultCodeOf(targetElement?.options);
			const n = typeof stored === 'number' ? stored : Number(stored);
			if (!code || !Number.isFinite(n)) return '';
			const amount = toMinor(n, code, 'round');
			if (amount === null) return '';
			// The API's transform refuses (blank, no warning) what the element would.
			try { return normalizeCurrency({ amount, currency: code }, targetElement?.options || {}) || ''; } catch (e) { return ''; }
		}
		default: return stored;
	}
}

/** One rendering of a stored element value, shared by every calculated surface. */
export function formatCalculatedValue(element, value) {
	if (value === undefined || value === null || value === '') return '—';
	if (Array.isArray(value)) return value.length ? value.map(entry => String(entry)).join(', ') : '—';
	if (element?.dataType === 'date') return formatDisplayDate(value);
	if (element?.dataType === 'date-time') return formatDisplayDateTime(value);
	if (element?.dataType === 'time') return formatDisplayTime(value) || '—';
	if (element?.dataType === 'measurement') return formatMeasurement(value, element?.options || {}) || '—';
	if (element?.dataType === 'currency') return formatCurrency(value, element?.options || {}) || '—';
	if (typeof value === 'boolean') return formatBooleanValue(element, value);
	// Money previewing into a target that is not a currency element (still
	// being chosen): no element context, so the unambiguous symbol.
	if (isCurrencyValue(value)) return formatCurrency(value, null) || '—';
	if (typeof value === 'object') {
		// A MEASUREMENT previewing into a target that is not a measurement
		// element — a duration from elapsed() shown before its transform, a
		// result whose element is still being chosen. The element's options
		// name no measure, and formatMeasurement with no measure renders
		// NOTHING at all, so read the measure off the value's own unit.
		const unit = getUnit(value?.unit);
		// An own unit (KPR-1109) away from its element: its number and its id's words.
		if (!unit && isOwnUnitId(value?.unit) && Number.isFinite(value?.value)) return `${value.value} ${value.unit.slice(4).replace(/-/g, ' ')}`;
		return (unit ? formatMeasurement(value, { measure: unit.measure, defaultUnit: unit.id }) : '') || '—';
	}
	return String(value);
}

/**
 * The formula in words: tokens replaced by element labels ("Volume A + Volume
 * B"). `columns` (KPR-686): a group column's own formula reads its entry's
 * columns first, labelled as the entry's.
 */
export function formulaSummary(text, mergedElements, getElementsForCard, { columns = null } = {}) {
	if (typeof text !== 'string') return '';
	const byName = new Map(namedElements(mergedElements).map(el => [el.name, el]));
	const columnByName = new Map(namedElements(columns).map(el => [el.name, el]));
	return text.replace(/\{\{([^{}]*)\}\}/g, (whole, inner) => {
		const path = inner.trim().split('.').map(s => s.trim());
		if (path.length === 1 && columnByName.has(path[0])) return labelOf(columnByName.get(path[0]));
		const first = byName.get(path[0]) || own(OWN_TERMINALS, path[0]);
		if (!first) return inner.trim();
		if (path.length === 1) return labelOf(first);
		if (isGroup(first)) {
			const column = groupColumnsOf(first).find(c => c.name === path[1]);
			return `${labelOf(first)} › ${column ? labelOf(column) : path[1]}`;
		}
		const walked = walkedElements(first, getElementsForCard).find(el => el.name === path[1]) || own(WALK_TERMINALS, path[1]);
		return `${labelOf(first)} › ${walked ? labelOf(walked) : path[1]}`;
	});
}

/** Do two stored values mean the same thing? (measurements compare on base) */
export function sameStoredValue(a, b) {
	const norm = v => (v === undefined || v === null ? '' : v);
	const x = norm(a);
	const y = norm(b);
	if (x === y) return true;
	if (x && y && typeof x === 'object' && typeof y === 'object' && typeof x.base === 'number' && typeof y.base === 'number') {
		return Math.abs(x.base - y.base) < 1e-9 && x.unit === y.unit;
	}
	// Money is exact: the same integer amount in the same currency (key order aside).
	if (isCurrencyValue(x) && isCurrencyValue(y)) return x.amount === y.amount && x.currency === y.currency;
	return JSON.stringify(x) === JSON.stringify(y);
}
